<template>
  <div class="asset-drawer__form">
    <UIPanel
      v-model="clearedSearch"
      v-model:storage="searchStorage"
      placeholder="Search transactions..."
    >
      <template #default="{ ButtonNew, ButtonExport, size, shrink }">
        <UIButton
          :label="shrink ? undefined : 'Go to transactions'"
          :leading="ArrowTopRightOnSquareIcon"
          variant="secondary"
          fill="light"
          :size
          @click="handleGoToTransactions"
        />
        <component
          :is="ButtonExport"
          :disabled
          :size
          @click="handleExport"
        />
        <template v-if="isAddButtonVisible">
          <UIButton
            v-if="isAIEnabled"
            :label="t('Ask AI')"
            variant="secondary"
            fill="outlined"
            size="sm"
            :leading="AIIcon"
            hide-label-to-sm
            @click="handleClickAskAI"
          />
          <UIDropdownList
            :data
            placement="bottom-end"
            @click:item="handleSelectType"
          >
            <template #default="{ toggle }">
              <component
                :is="ButtonNew"
                size="sm"
                @click="toggle"
              />
            </template>
          </UIDropdownList>
        </template>
      </template>
    </UIPanel>
  </div>
  <UIGridSkeleton
    v-if="disabled"
    :columns
    :sm
    message="Loading transactions..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    :columns
    :items
    :sm
    scrollable
  >
    <template #actions="{ item, size }">
      <UIButton
        v-if="item"
        :label="editButtonLabel"
        :leading="editButtonIcon"
        variant="secondary"
        fill="light"
        :size
        @click="handleClickEditTransaction(item)"
      />
    </template>
    <template #cellEntries0AccountId="{ item }">
      <UIAssetInfo
        :id="item.entries[0]?.account_id"
        :line-clamp="2"
        :clickable="item.entries[0]?.account_id !== instance.id"
      />
    </template>
    <template #cellEntries0Amount="{ displayValue }">
      <span :class="getAmountCellClass(displayValue)">{{ displayValue }}</span>
    </template>
    <template #cellEntries0AssetId="{ item }">
      <UIAssetInfo
        :id="item.entries[0]?.asset_id"
        :line-clamp="2"
        :clickable="item.entries[0]?.asset_id !== instance.id"
      />
    </template>
    <template #cellEntries1AccountId="{ item }">
      <UIAssetInfo
        :id="item.entries[1]?.account_id"
        :line-clamp="2"
        :clickable="item.entries[1]?.account_id !== instance.id"
      />
    </template>
    <template #cellEntries1Amount="{ displayValue }">
      <span :class="getAmountCellClass(displayValue)">{{ displayValue }}</span>
    </template>
    <template #cellEntries1AssetId="{ item }">
      <UIAssetInfo
        :id="item.entries[1]?.asset_id"
        :line-clamp="2"
        :clickable="item.entries[1]?.asset_id !== instance.id"
      />
    </template>
  </UIGrid>
  <UIPagination
    :disabled
    :page-no
    :page-size="pageSize"
    :total="pagination?.total"
    class="asset-drawer__pagination"
    @click:page="handleClickPage"
    @update:page-size="handleUpdatePageSize"
  />
</template>

<script setup lang="ts">
import { computed, inject, markRaw, nextTick, onMounted, onWatcherCleanup, ref, watch } from 'vue'
import { get, isEqual } from 'lodash'
import { debouncedWatch, watchPausable } from '@vueuse/core'
import { useRouter } from 'vue-router'

import {
  Transaction,
  PaginatedMeta,
  Sort,
  ReadonlyMode,
  AssetClass,
  TransactionEntry,
} from '@types'
import { TransactionClass } from '@/entities/transactions'
import { EntityEvent } from '@/entities/utils/enums'

import {
  ASSET_TRANSACTIONS_PAGE_SIZE,
  DEBOUNCE_DELAY,
  READONLY_MODE,
} from '@/const/common'
import { ASSET_CURRENCY_TYPE, ASSET_FIELD } from '../utils/const'
import {
  ROUTE_NAME,
  TRANSACTION_ENTRY_FIELD,
  TRANSACTION_ENTRY_FIELD_LABEL,
  TRANSACTION_FIELD,
  TRANSACTION_FIELD_LABEL,
  TRANSACTIONS_FILTER_KEY,
  TRANSACTIONS_SEARCH_KEY,
  TRANSACTIONS_SORT_KEY,
} from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { getAccountCurrency } from './utils/helpers'
import { amountTooltip, displayAmount } from '@/entities/transactions/utils/helpers'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'
import { useHandledGridExport } from '@/components/hooks/gridExport'
import { useLocale } from '@/plugins/i18n'
import { mapTransactionsSort } from '@/views/Data/Transactions/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useSettingsStore } from '@/store/settings'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'
import { useTransactionsBunchStore } from '@/store/transactions/bunch'

import AIAgent from '@/modules/AIAgent/AIAgent.vue'

import { UIButton } from '@ui/buttons'
import { UIDropdownList } from '@ui/dropdowns'
import { UIAssetInfo, UIPagination, UIPanel } from '@ui/core'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import AIIcon from '@/assets/images/icons/ai.svg'
import { ArrowTopRightOnSquareIcon, EyeIcon, PencilIcon } from '@heroicons/vue/24/outline'

type Props = {
  instance: AssetClass
  amount?: number
  path?: string[]
  isLeaf?: boolean
}

type Emits = {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ inheritAttrs: false })

const { t } = useLocale('layouts.Main')

const router = useRouter()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const settingsStore = useSettingsStore()
const transactionsStore = useTransactionsStore()
const transactionsBunchStore = useTransactionsBunchStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const items = ref<Transaction[]>([])
const pagination = ref<PaginatedMeta>()

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()
const pageNo = ref(0)
const pageSize = ref(ASSET_TRANSACTIONS_PAGE_SIZE)
const isLoading = ref(false)

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const isAIEnabled = computed(() => settingsStore.getAIAgent)

const editButtonLabel = computed(() =>
  isReadonly?.value ? 'View transaction' : 'Edit transaction',
)
const editButtonIcon = computed(() =>
  isReadonly?.value ? EyeIcon : PencilIcon,
)
const disabled = computed(
  () => isLoading.value || !transactionsSettingsStore.initFlag,
)

const settingsList = computed(() => transactionsSettingsStore.getList)

const transactionTypes = computed<string[]>(() => {
  const type = props.instance.field<string>(ASSET_FIELD.TYPE).value
  return settingsList.value.filter(item => {
    return item.entries.some(entry =>
      entry.account?.includes?.includes(type) ||
      entry.asset?.includes?.includes(type)
    )
  }).map(item => item.name)
})

const data = computed(() =>
  transactionTypes.value?.map(value => ({
    value,
    label: value,
  })),
)

const sm = '1fr'
const columns = computed(() => [
  {
    name: 'type',
    caption: 'Type',
  },
  {
    name: TRANSACTION_FIELD.DATE,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE],
    filter: {
      isDate: true,
    },
  },
  {
    name: 'entries.0.account_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID],
    filter: { name: 'entry_account_name__pos_id-0' },
    disableTooltip: true,
  },
  {
    name: 'entries.0.amount',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
    cellValueClasses: 'ui-grid__cell--right number-node',
    filter: { name: 'entry_amount__pos_id-0' },
    formatter: amount1Formatter,
    tooltip: amountTooltipHandler,
  },
  {
    name: 'entries.0.asset_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID],
    filter: { name: 'entry_asset_name__pos_id-0' },
    disableTooltip: true,
  },
  {
    name: 'entries.1.account_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID],
    filter: { name: 'entry_account_name__pos_id-1' },
    disableTooltip: true,
  },
  {
    name: 'entries.1.amount',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
    cellValueClasses: 'ui-grid__cell--right number-node',
    filter: { name: 'entry_amount__pos_id-1' },
    formatter: amount2Formatter,
    tooltip: amountTooltipHandler,
  },
  {
    name: 'entries.1.asset_id',
    caption: TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID],
    filter: { name: 'entry_asset_name__pos_id-1' },
    disableTooltip: true,
  },
  {
    name: TRANSACTION_FIELD.DESCRIPTION,
    caption: TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DESCRIPTION],
  },
])

const mappedSort = computed(() => mapTransactionsSort(sort.value?.map((item, index) => ({ ...item, index }))))

const fetchOptions = computed(() => ({
  search: clearedSearch.value,
  sort: mappedSort.value,
  asset_id: props.instance.id,
}))

const isAddButtonVisible = computed(() => !!props.path?.length && !!transactionTypes.value.length)

const getAmountCellClass = (value: string) => ({
  'grid-cell__amount--positive': !value.startsWith('-'),
  'grid-cell__amount--negative': value.startsWith('-'),
})

const amountFormatter = (value: number | null | undefined, data: Transaction, index: number) => {
  const settings = settingsList.value.find(item => item.name === data.type)?.entries[index]?.amount
  if (!settings) return ''
  const isPercent = settings?.rule === TransactionAmountRule.PERCENT
  return displayAmount(value, isPercent)
}
const amount1Formatter = (value: number | null | undefined, data: Transaction) => {
  return amountFormatter(value, data, 0)
}
const amount2Formatter = (value: number | null | undefined, data: Transaction) => {
  return amountFormatter(value, data, 1)
}

const amountTooltipHandler = (field: string, data: Transaction) => {
  if (!data) return
  const fieldIndex = Number(field.replace(/^entries\.(\d+?)\.amount/, '$1'))
  const settings = settingsList.value.find(item => item.name === data.type)
  const value = data.entries[fieldIndex]?.amount
  if (value === null || value === undefined) return
  const isPercent = settings?.entries[fieldIndex]?.amount?.rule === TransactionAmountRule.PERCENT
  return amountTooltip(value, isPercent)
}

const fetch = async (signal?: AbortSignal) => {
  try {
    isLoading.value = true
    const response = await transactionsStore.fetch(
      {
        ...fetchOptions.value,
        page_no: pageNo.value,
        page_size: pageSize.value,
      },
      false,
      signal,
      false,
    )
    items.value = response.data
    pagination.value = response.meta
  } catch (e) {
    handleCatchedError(e as string, fetchOptions.value)
  } finally {
    isLoading.value = false
  }
}

const handleClickPage = (page: number) => {
  pageNo.value = page
}

const handleUpdatePageSize = (newPageSize: number) => {
  if (newPageSize === pageSize.value) return

  pausePageNo()
  pageNo.value = 0
  nextTick(resumePageNo)
  pageSize.value = newPageSize
}

const handleClickEditTransaction = (transaction: Transaction) => {
  let instance = transactionsBunchStore.getElementById(transaction.id)
  if (!instance) {
    instance = new TransactionClass(transaction as any)
  }
  const modalInstance = modalsStore.init(instance.id, instance, { allowTray: true })
  const onUpdated = () => {
    fetch()
    instance.removeEventListener(EntityEvent.UPDATED, onUpdated)
  }
  instance.addEventListener(EntityEvent.UPDATED, onUpdated)
  modalInstance?.open(modalsStore.getZIndex())
}

const handleGoToTransactions = () => {
  emit('close')
  const uiSettings = repositoriesStore.getCurrentUISettings

  const currentSearch = get(uiSettings, TRANSACTIONS_SEARCH_KEY)
  const currentFilter = get(uiSettings, TRANSACTIONS_FILTER_KEY)
  const currentSort = get(uiSettings, TRANSACTIONS_SORT_KEY)

  const name = props.instance.field<string>(ASSET_FIELD.NAME).value

  if (
    currentSearch === name &&
    !currentFilter &&
    currentSort.length === 0 &&
    router.currentRoute.value.name === ROUTE_NAME.TRANSACTIONS
  )
    return

  repositoriesStore.setCurrentUISettings([
    [TRANSACTIONS_SEARCH_KEY, name],
    [TRANSACTIONS_FILTER_KEY, undefined],
    [TRANSACTIONS_SORT_KEY, []],
  ])
  transactionsStore.clear()
  router.push({ name: ROUTE_NAME.TRANSACTIONS })
}

const getAcountAsset = () => {
  const accountId = props.isLeaf ? props.path?.at(-2) : props.path?.at(-1)
  let amount = props.amount || null
  const assetId = props.isLeaf ? props.path?.at(-1) : ''
  return { accountId, amount, assetId }
}

const handleSelectType = (type: string) => {
  // get chosen transaction type settings
  const settings = settingsList.value.find(item => item.name === type)
  if (!settings) return
  // get account/asset ids from path and amount
  let { accountId, amount, assetId } = getAcountAsset()
  if (type === 'sell' && amount) {
    amount = amount > 0 ? -amount : amount
  }
  // create new transaction instance
  const instance = transactionsBunchStore.createElement()
  const entries: TransactionEntry[] = [
    {
      account_id: '',
      amount: null,
      asset_id: '',
    },
    {
      account_id: '',
      amount: null,
      asset_id: '',
    },
  ]
  // fill account/asset ids and amounts for chosen transaction type
  const assetType = props.instance.field<string>(ASSET_FIELD.TYPE).value
  const entryIndex = settings.entries.findIndex(entry => entry.account?.includes?.includes(assetType) || entry.asset?.includes?.includes(assetType))
  if (entryIndex !== -1) {
    entries[entryIndex].account_id = accountId
    entries[entryIndex].amount = amount
    entries[entryIndex].asset_id = assetId
  }
  // fill currency asset id if this is needed
  const currencyIndex = settings.entries.findIndex(entry => entry.asset?.includes?.includes(ASSET_CURRENCY_TYPE))
  if (accountId && currencyIndex !== -1 && !entries[currencyIndex].asset_id) {
    entries[currencyIndex].asset_id = getAccountCurrency(accountId, assetsBunchStore)
  }
  // set transaction instance data
  instance.set({
    type,
    entries,
  })
  const modalInstance = modalsStore.init(instance.id, instance, { allowTray: true })
  modalInstance?.open(modalsStore.getZIndex(), {
    restrictions: transactionTypes.value,
  })
}

const handleClickAskAI = () => {
  const { accountId, amount, assetId } = getAcountAsset()
  let accountInstance = accountId ? assetsBunchStore.getElementById(accountId) : undefined
  let assetInstance = assetId ? assetsBunchStore.getElementById(assetId) : undefined
  let accountName = accountInstance?.field<string>(ASSET_FIELD.NAME).value
  let accountType = accountInstance?.field<string>(ASSET_FIELD.TYPE).value
  let assetName = assetInstance?.field<string>(ASSET_FIELD.NAME).value
  let assetType = assetInstance?.field<string>(ASSET_FIELD.TYPE).value
  let initMessage
  if (assetInstance && accountInstance) {
    initMessage = `I want to create a transaction for the ${assetType} "${assetName}", which belongs to ${accountType} "${accountName}"${amount ? ` with amount ${amount}` : ''}`
  } else if (assetInstance) {
    initMessage = `I want to create a transaction for the ${assetType} "${assetName}"${amount ? ` with amount ${amount}` : ''}`
  } else if (accountInstance) {
    initMessage = `I want to create a transaction for the ${accountType} "${accountName}"`
  } else {
    initMessage = 'I want to create a transaction'
  }
  const modalInstance = modalsStore.init('ask-ai', markRaw(AIAgent))
  modalInstance?.open(modalsStore.getZIndex(), {
    initMessage
  })
}

const handleExport = () => {
  const assetName = props.instance.field<string>(ASSET_FIELD.NAME).value
  useHandledGridExport(columns.value, items.value, `${assetName}_transactions`, true)
}

const { pause: pausePageNo, resume: resumePageNo } = watchPausable(
  pageNo,
  () => {
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
  },
)

watch(pageSize, () => {
  const controller = new AbortController()
  fetch(controller.signal)
  onWatcherCleanup(() => {
    controller.abort()
  })
})

debouncedWatch(
  fetchOptions,
  (value, prev) => {
    if (isEqual(value, prev)) return
    const controller = new AbortController()
    fetch(controller.signal)
    onWatcherCleanup(() => {
      controller.abort()
    })
  },
  {
    deep: true,
    debounce: DEBOUNCE_DELAY,
  },
)

onMounted(() => {
  fetch()
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>

<style scoped>
.grid-cell__amount {
  &--positive {
    @apply text-slate-950 dark:text-green-400;
  }

  &--negative {
    @apply text-red-600 dark:text-red-400;
  }
}
</style>
